import React, { useState, useMemo } from 'react';
import { X, Package, Ruler, Tag, ChevronLeft, ChevronRight, Play, Info, Check, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { getColorHex } from '../lib/colors';

const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

/**
 * ProductDetailsModal — popup rico com fotos, cores, tamanhos e preços.
 * Usado em "Gerar Pedido" (Cliente + Vendedor).
 *
 * Features:
 *   - Carrossel de fotos + vídeos com thumbnails
 *   - Clique numa cor pula para a foto vinculada (via cores_foto_map)
 *   - Seleção de cor e tamanho antes de adicionar ao carrinho
 *   - Tabela de preços por tipo (só mostra os que têm valor > 0)
 *   - Accordions colapsáveis para Descrição e Especificações
 *   - Layout compacto e rolável verticalmente
 *   - onAdd recebe {product, cor, tamanho}
 */

// Componente auxiliar: Accordion Section
function AccordionSection({ title, defaultOpen = true, children, testId, paletteText }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition"
        data-testid={testId}
      >
        <span className={`text-xs uppercase font-bold tracking-wider ${paletteText}`}>{title}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && (
        <div className="px-3 py-2.5 bg-white dark:bg-gray-900">
          {children}
        </div>
      )}
    </div>
  );
}

export default function ProductDetailsModal({ product, onClose, onAdd, color = 'blue', showPrice = true }) {
  const [mediaIdx, setMediaIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [videoIdx, setVideoIdx] = useState(0);
  const cores = useMemo(() => product?.cores || [], [product]);
  const coresFotoMap = useMemo(() => product?.cores_foto_map || {}, [product]);
  const tamanhosDisponiveis = useMemo(() => {
    const raw = product?.tamanho || '';
    if (!raw) return [];
    return raw.split(/[,|/]/).map(s => s.trim()).filter(Boolean);
  }, [product]);
  const [selectedCor, setSelectedCor] = useState(cores[0] || '');
  const [selectedTamanho, setSelectedTamanho] = useState(tamanhosDisponiveis[0] || '');
  const variacoes = useMemo(() => Array.isArray(product?.variacoes) ? product.variacoes : [], [product]);
  const [selectedVariacao, setSelectedVariacao] = useState(null); // {codigo, imagem, preco}

  if (!product) return null;
  const fotos = product.fotos || [];
  const videos = product.videos || [];
  const fotosEspec = product.fotos_especificacoes || [];
  // Se uma variação está selecionada, mostra a foto dela no topo do carrossel
  const allImages = selectedVariacao?.imagem
    ? [selectedVariacao.imagem, ...fotos, ...fotosEspec]
    : [...fotos, ...fotosEspec];
  const hasMedia = allImages.length + videos.length > 0;

  const handleVariacaoClick = (v) => {
    setSelectedVariacao(v);
    setShowVideo(false);
    setMediaIdx(0); // sempre mostra a foto da variação selecionada
  };

  const handleCorClick = (cor) => {
    setSelectedCor(cor);
    const fotoCor = coresFotoMap[cor];
    if (!fotoCor) return;
    const idx = allImages.findIndex(img => img === fotoCor || img.endsWith(fotoCor) || fotoCor.endsWith(img));
    if (idx >= 0) { setShowVideo(false); setMediaIdx(idx); }
  };

  const paletteBg = color === 'emerald' ? 'from-emerald-600 to-teal-600' : 'from-blue-600 to-indigo-600';
  const paletteBorder = color === 'emerald' ? 'border-emerald-500' : 'border-blue-500';
  const paletteText = color === 'emerald' ? 'text-emerald-600' : 'text-blue-600';

  const precos = [
    { key: 'valor_por_unidade', label: 'Unidade' },
    { key: 'valor_por_caixa', label: 'Caixa' },
    { key: 'valor_pacote', label: 'Pacote' },
    { key: 'valor_por_kg', label: 'KG' },
    { key: 'valor_por_100', label: 'Cento' },
    { key: 'valor_por_1000', label: 'Milheiro' },
  ].filter(p => Number(product[p.key]) > 0);

  const handleAddClick = () => {
    if (onAdd) {
      onAdd(product, {
        cor: selectedCor,
        tamanho: selectedTamanho,
        variacao: selectedVariacao ? selectedVariacao.codigo : '',
        variacao_imagem: selectedVariacao ? selectedVariacao.imagem : '',
        variacao_preco: selectedVariacao && Number(selectedVariacao.preco) > 0 ? Number(selectedVariacao.preco) : null,
        variacao_descricao: selectedVariacao ? (selectedVariacao.descricao || '') : '',
      });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-2 sm:p-4 animate-in fade-in" onClick={onClose} data-testid="product-details-modal">
      <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header — sticky compacto */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Info className={`w-4 h-4 flex-shrink-0 ${paletteText}`} />
            <h3 className="text-base font-bold truncate" data-testid="product-details-title">{product.produto}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg" data-testid="product-details-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Corpo rolável */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {/* Media — 2/5 do layout */}
            <div className="lg:col-span-2 bg-gray-100 dark:bg-gray-800 flex flex-col">
              <div className="relative aspect-square lg:aspect-auto lg:min-h-[280px] lg:max-h-[340px] flex items-center justify-center">
                {!hasMedia ? (
                  <div className="flex flex-col items-center text-gray-400">
                    <Package className="w-16 h-16" />
                    <span className="text-xs mt-2">Sem foto</span>
                  </div>
                ) : showVideo && videos[videoIdx] ? (
                  <video src={videos[videoIdx]} controls autoPlay className="w-full h-full object-contain bg-black" />
                ) : (
                  <img src={allImages[mediaIdx]} alt={product.produto} className="w-full h-full object-contain p-3" />
                )}
                {(allImages.length > 1 && !showVideo) && (
                  <>
                    <button onClick={() => setMediaIdx(i => i > 0 ? i - 1 : allImages.length - 1)} className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-900/90 rounded-full p-1.5 shadow-md hover:scale-110 transition" data-testid="media-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => setMediaIdx(i => i < allImages.length - 1 ? i + 1 : 0)} className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-white/90 dark:bg-gray-900/90 rounded-full p-1.5 shadow-md hover:scale-110 transition" data-testid="media-next">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              {/* Thumbnails */}
              {(allImages.length > 1 || videos.length > 0) && (
                <div className="p-1.5 flex gap-1 overflow-x-auto bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                  {allImages.map((img, i) => (
                    <button key={i} onClick={() => { setShowVideo(false); setMediaIdx(i); }} className={`flex-shrink-0 w-10 h-10 rounded-md overflow-hidden border-2 ${!showVideo && mediaIdx === i ? paletteBorder : 'border-transparent'}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                  {videos.map((_, i) => (
                    <button key={`v-${i}`} onClick={() => { setVideoIdx(i); setShowVideo(true); }} className={`flex-shrink-0 w-10 h-10 rounded-md bg-gray-900 flex items-center justify-center border-2 ${showVideo && videoIdx === i ? paletteBorder : 'border-transparent'}`}>
                      <Play className="w-3.5 h-3.5 text-white" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info + Seletores — 3/5 do layout */}
            <div className="lg:col-span-3 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider">Código</p>
                  <p className="font-mono text-sm font-bold">{product.codigo}</p>
                </div>
                {product.categoria && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${paletteText} bg-gray-100 dark:bg-gray-800`}>
                    <Tag className="w-3 h-3" />
                    {product.categoria}
                  </span>
                )}
              </div>

              {/* Seletor de variações — LISTA compacta com preço próprio (fallback preço padrão) */}
              {variacoes.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-1.5">
                    Variação ({variacoes.length}):{' '}
                    <span className={`${paletteText} normal-case font-bold`}>
                      {selectedVariacao?.codigo || 'selecione uma opção'}
                    </span>
                  </p>
                  <div className="max-h-[240px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700" data-testid="product-variacoes-list">
                    {variacoes.map((v, i) => {
                      const isActive = selectedVariacao?.codigo === v.codigo;
                      const precoVar = Number(v.preco) > 0 ? Number(v.preco) : null;
                      return (
                        <button
                          key={v.codigo || i}
                          type="button"
                          onClick={() => handleVariacaoClick(v)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 text-left transition ${isActive ? `${color === 'emerald' ? 'bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500' : 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'}` : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-l-4 border-transparent'}`}
                          data-testid={`product-variacao-${(v.codigo || i).toString().toLowerCase()}`}
                          title={v.descricao || v.codigo}
                        >
                          <div className="w-9 h-9 rounded border border-gray-200 dark:border-gray-600 bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                            {v.imagem ? (
                              <img src={v.imagem} alt={v.codigo} className="w-full h-full object-contain" loading="lazy" />
                            ) : (
                              <Package className="w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={`text-xs font-mono font-bold truncate ${isActive ? paletteText : ''}`}>{v.codigo}</div>
                            {v.descricao && <div className="text-[10px] text-gray-500 truncate italic">{v.descricao}</div>}
                          </div>
                          {precoVar !== null && (
                            <span className={`text-[11px] font-bold tabular-nums flex-shrink-0 ${paletteText}`} data-testid={`variacao-preco-${v.codigo}`}>
                              {fmtBRL(precoVar)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1 italic">💡 Clique para trocar a foto e usar o preço específico dessa variação.</p>
                </div>
              )}

              {/* Seletor de cores */}
              {cores.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-1.5">
                    Cor: <span className={`${paletteText} normal-case`}>{selectedCor || 'selecione'}</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {cores.map((c, i) => {
                      const hex = getColorHex(c);
                      const isActive = selectedCor === c;
                      return (
                        <button
                          key={i}
                          onClick={() => handleCorClick(c)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border-2 transition ${isActive ? `${paletteBorder} bg-gray-100 dark:bg-gray-800` : 'border-gray-300 dark:border-gray-600 hover:border-gray-500'}`}
                          data-testid={`product-cor-${c.toLowerCase().replace(/\s/g, '-')}`}
                          title={coresFotoMap[c] ? 'Clique para ver a foto dessa cor' : c}
                        >
                          <span className="w-3.5 h-3.5 rounded-full border border-gray-300" style={{ backgroundColor: hex }} />
                          {c}
                          {isActive && <Check className="w-3 h-3" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Seletor de tamanho */}
              {tamanhosDisponiveis.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-1.5 flex items-center gap-1">
                    <Ruler className="w-3 h-3" />
                    Tamanho: <span className={`${paletteText} normal-case`}>{selectedTamanho}</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {tamanhosDisponiveis.map((t, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedTamanho(t)}
                        className={`min-w-[38px] px-2.5 py-1 rounded-md text-xs font-bold border-2 transition ${selectedTamanho === t ? `${paletteBorder} ${paletteText} bg-gray-100 dark:bg-gray-800` : 'border-gray-300 dark:border-gray-600 hover:border-gray-500'}`}
                        data-testid={`product-tam-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Preços — sempre visível pois é crítico para venda */}
              {showPrice && precos.length > 0 && (
                <div>
                  <p className="text-[10px] text-gray-500 uppercase font-semibold tracking-wider mb-1.5">Preços</p>
                  <div className="space-y-1">
                    {precos.map(p => (
                      <div key={p.key} className="flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-md px-2.5 py-1">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Por {p.label}</span>
                        <span className={`text-sm font-black tabular-nums ${paletteText}`} data-testid={`preco-${p.key}`}>
                          {fmtBRL(product[p.key])}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Descrição — accordion */}
              {product.descricao && (
                <AccordionSection title="Descrição" defaultOpen={true} testId="accordion-descricao" paletteText={paletteText}>
                  <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{product.descricao}</p>
                </AccordionSection>
              )}

              {/* Especificações — accordion */}
              {product.especificacoes && (
                <AccordionSection title="Especificações Técnicas" defaultOpen={true} testId="accordion-especificacoes" paletteText={paletteText}>
                  <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">{product.especificacoes}</p>
                </AccordionSection>
              )}
            </div>
          </div>
        </div>

        {/* Footer — sticky */}
        {onAdd && (
          <div className="px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-shrink-0 bg-white dark:bg-gray-900">
            <button onClick={onClose} className="px-3 py-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-xs font-medium">
              Fechar
            </button>
            <button
              onClick={handleAddClick}
              className={`bg-gradient-to-r ${paletteBg} text-white px-4 py-1.5 rounded-md font-bold text-xs shadow-md hover:shadow-lg transition flex items-center gap-1.5`}
              data-testid="product-details-add-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              Adicionar ao Pedido
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
